import React, { FC } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface TrueFalseDonutProps {
  trueCount: number;
  falseCount: number;
  /** Outer diameter in px. */
  size?: number;
}

/** Point on a circle for an angle in degrees (0° = 3 o'clock, clockwise). */
const polar = (cx: number, cy: number, r: number, angleDeg: number): [number, number] => {
  const a = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

/**
 * Compact True/False pie for a post's reaction split: a solid accent wedge for
 * the True share over a muted False disc. Hidden while a post has no reactions;
 * the exact split is available on hover.
 */
const TrueFalseDonut: FC<TrueFalseDonutProps> = ({ trueCount, falseCount, size = 18 }) => {
  const theme = useTheme();

  const total = trueCount + falseCount;
  if (total === 0) return null;

  const truePct = trueCount / total;
  const percentLabel = Math.round(truePct * 100);
  const tooltip = `${percentLabel}% favorable · ${total} ${total === 1 ? 'reaction' : 'reactions'}`;

  const r = size / 2;
  const cx = r;
  const cy = r;
  const trueColor = theme.tokens.colors.primary;
  const falseColor = theme.tokens.colors.text4;

  let slices: React.ReactNode;
  if (truePct >= 0.999) {
    slices = <circle cx={cx} cy={cy} r={r} fill={trueColor} />;
  } else if (truePct <= 0.001) {
    slices = <circle cx={cx} cy={cy} r={r} fill={falseColor} />;
  } else {
    // False disc underneath, True wedge on top (starts at 12 o'clock, clockwise).
    const [sx, sy] = polar(cx, cy, r, -90);
    const [ex, ey] = polar(cx, cy, r, -90 + 360 * truePct);
    const largeArc = truePct > 0.5 ? 1 : 0;
    const wedge = `M ${cx} ${cy} L ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey} Z`;
    slices = (
      <>
        <circle cx={cx} cy={cy} r={r} fill={falseColor} />
        <path d={wedge} fill={trueColor} />
      </>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={tooltip}>
          {slices}
        </svg>
      </Box>
    </Tooltip>
  );
};

export default TrueFalseDonut;
